import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api, MATERIALS_QUERY_KEY } from "@/api/client";
import { useAlertSubscriptions } from "./useAlertSubscriptions";

/** Preferences-page body for the "Signal alerts" SettingsCard: one
 * switch per enabled material. The card title/description come from the
 * parent (PreferencesPage) so this stays a pure body component. */
export function AlertSubscriptionsCard({ email }: { email: string | null }) {
  const { t } = useTranslation("preferences");
  const { subscribed, locked, loading, toggle } = useAlertSubscriptions();
  const materials = useQuery({
    queryKey: MATERIALS_QUERY_KEY,
    queryFn: api.listMaterials,
    staleTime: 300_000,
  });

  if (locked) {
    return (
      <div className="flex flex-col gap-1">
        <p className="text-sm text-muted-foreground">{t("alerts.locked")}</p>
        <Link to="/account/billing" className="text-sm underline">{t("alerts.upgradeCta")}</Link>
      </div>
    );
  }

  const enabledMaterials = (materials.data?.materials ?? []).filter(
    (m) => m.enabled,
  );

  return (
    <div className="flex flex-col gap-2">
      {email ? (
        <p className="text-sm text-muted-foreground">
          {t("alerts.sentTo", { email })}
        </p>
      ) : null}
      {enabledMaterials.map((m) => {
        const isOn = subscribed.has(m.material_id);
        return (
          <div
            key={m.material_id}
            className="flex items-center justify-between gap-2"
          >
            <span className="text-sm">{m.display_name || m.material_id}</span>
            <button
              type="button"
              role="switch"
              aria-checked={isOn}
              aria-label={m.display_name || m.material_id}
              disabled={loading}
              onClick={() => void toggle(m.material_id)}
              className={
                isOn
                  ? "h-5 w-9 rounded-full bg-primary transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                  : "h-5 w-9 rounded-full bg-muted transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
              }
            >
              <span
                className={
                  isOn
                    ? "block h-4 w-4 translate-x-4 rounded-full bg-background transition-transform"
                    : "block h-4 w-4 translate-x-0.5 rounded-full bg-background transition-transform"
                }
              />
            </button>
          </div>
        );
      })}
    </div>
  );
}
